import { useEffect } from 'react';
import { cn } from '@/utils/cn';

const ADSENSE_CLIENT = import.meta.env.VITE_ADSENSE_CLIENT;

let scriptInjected = false;

function injectAdSenseScript(): void {
  if (scriptInjected || !ADSENSE_CLIENT) return;
  scriptInjected = true;
  // The loader is already in index.html <head> for site verification; only add
  // it here as a fallback if that tag is somehow absent (avoids a double load).
  if (document.querySelector('script[src*="pagead/js/adsbygoogle.js"]')) return;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
  script.crossOrigin = 'anonymous';
  document.head.appendChild(script);
}

export interface AdSlotProps {
  slot?: string;
  format?: string;
  className?: string;
}

/**
 * Google AdSense unit. Renders a subtle placeholder until
 * VITE_ADSENSE_CLIENT and a slot id are configured.
 */
export function AdSlot({ slot, format = 'auto', className }: AdSlotProps) {
  const enabled = Boolean(ADSENSE_CLIENT && slot);

  useEffect(() => {
    if (!enabled) return;
    injectAdSenseScript();
    try {
      const w = window as unknown as { adsbygoogle?: unknown[] };
      (w.adsbygoogle = w.adsbygoogle ?? []).push({});
    } catch {
      // Ad blockers throw here; nothing to do.
    }
  }, [enabled, slot]);

  if (!enabled) {
    return (
      <div
        className={cn(
          'glass flex h-24 items-center justify-center text-[11px] font-medium uppercase tracking-[0.2em] text-zinc-600',
          className
        )}
        aria-hidden
      >
        Advertisement
      </div>
    );
  }

  return (
    <ins
      className={cn('adsbygoogle block', className)}
      style={{ display: 'block' }}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={slot}
      data-ad-format={format}
      data-full-width-responsive="true"
    />
  );
}
